import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef } from "react";
import { Calendar } from "lucide-react";

const CALENDLY_URL = "https://calendly.com/brandon-leafrushmarketing/website?hide_gdpr_banner=1&primary_color=2f7f34";
const CALENDLY_IFRAME_URL = "https://calendly.com/brandon-leafrushmarketing/website?hide_gdpr_banner=1&primary_color=2f7f34&embed_domain=leafrushmarketing.com&embed_type=Inline";

interface CalendlyEmbedProps {
  variant?: "default" | "minimal";
}

export const CalendlyEmbed = ({ variant = "default" }: CalendlyEmbedProps) => {
  const isMinimal = variant === "minimal";
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use Intersection Observer to detect when component is near viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Load iframe when 200px from viewport
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "200px", // Start loading 200px before visible
        threshold: 0
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={isMinimal ? "" : "py-16 bg-accent/20"}>
      <div className="container px-4 md:px-6">
        <div className="mx-auto max-w-4xl">
          {!isMinimal && (
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold mb-3 text-muted-foreground">Prefer to talk?</h3>
              <p className="text-base text-muted-foreground">
                Schedule a call to discuss your business
              </p>
            </div>
          )}
          
          <div className="bg-card rounded-2xl overflow-hidden shadow-sm border">
            <div style={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
              {shouldLoad ? (
                <iframe
                  src={CALENDLY_IFRAME_URL}
                  width="100%"
                  height="700"
                  frameBorder="0"
                  scrolling="no"
                  title="Schedule a call with Leaf Rush Marketing"
                  allow="payment"
                  loading="lazy"
                />
              ) : (
                // Placeholder before iframe loads
                <div 
                  className="flex flex-col items-center justify-center bg-muted/30"
                  style={{ width: '100%', height: '700px' }}
                >
                  <Calendar className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-lg mb-4">Loading scheduler...</p>
                  <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                    <Button variant="default">Open Calendly</Button>
                  </a>
                </div>
              )}
            </div>
          </div>
          
          {/* Backup link */}
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground mb-2">
              Having trouble with the scheduler?
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button variant="outline">Open Calendly in New Tab</Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
